import React, { useEffect, useState } from 'react';
import { Table, Button, Space, Modal, Form, Input, Select, Switch, message, Tag, Popconfirm, Card, Typography } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, ReloadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { getMqttChannels, createMqttChannel, updateMqttChannel, deleteMqttChannel, getMqttServers, refreshSubscriptions, MqttChannel, ChannelResult } from '../api/mqtt';

interface MqttServer {
  id: number;
  name: string;
  url: string;
  port: number;
  enabled: boolean;
  isDefault: boolean;
}

const MqttSubscriptionManagement: React.FC = () => {
  const [channels, setChannels] = useState<MqttChannel[]>([]);
  const [servers, setServers] = useState<MqttServer[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [changingId, setChangingId] = useState<number | null>(null);
  const [visible, setVisible] = useState(false);
  const [editing, setEditing] = useState<MqttChannel | null>(null);
  const [form] = Form.useForm();

  const load = async () => {
    setLoading(true);
    try {
      const [channelRes, serverRes] = await Promise.all([getMqttChannels(), getMqttServers()]);
      setChannels(channelRes.data);
      setServers(serverRes.data);
    } catch {
      message.error('加载频道失败');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const report = (refresh: ChannelResult['refresh'], successText: string) => {
    if (refresh.some(item => !item.success)) {
      message.warning(`${successText}，部分服务器尚未加载订阅：${refresh.filter(item => !item.success).map(item => item.message).join('；')}`);
    } else message.success(successText);
  };
  const errorText = (error: any) => {
    const detail = error.response?.data?.message;
    return Array.isArray(detail) ? detail.join('；') : detail || '操作失败';
  };
  const open = (record?: MqttChannel) => {
    setEditing(record || null);
    form.resetFields();
    form.setFieldsValue(record || { serverId: servers.find(server => server.enabled && server.isDefault)?.id });
    setVisible(true);
  };
  const save = async () => {
    let values;
    try { values = await form.validateFields(); } catch { return; }
    setSaving(true);
    try {
      const data = { serverId: values.serverId, channelName: values.channelName.trim(), enabled: editing?.enabled ?? true };
      const res = editing ? await updateMqttChannel(editing.id, data) : await createMqttChannel(data);
      report(res.data.refresh, editing ? '频道已更新' : '频道已添加');
      setVisible(false);
      await load();
    } catch (error) { message.error(errorText(error)); }
    finally { setSaving(false); }
  };
  const toggle = async (record: MqttChannel, enabled: boolean) => {
    setChangingId(record.id);
    try {
      const res = await updateMqttChannel(record.id, { serverId: record.serverId, channelName: record.channelName, enabled });
      report(res.data.refresh, enabled ? '频道已启用' : '频道已禁用');
      await load();
    } catch (error) { message.error(errorText(error)); }
    finally { setChangingId(null); }
  };
  const remove = async (record: MqttChannel) => {
    try {
      const res = await deleteMqttChannel(record.id);
      report(res.data.refresh, '频道已删除');
      await load();
    } catch (error) { message.error(errorText(error)); }
  };
  const retry = async (record: MqttChannel) => {
    setChangingId(record.id);
    try {
      const res = await refreshSubscriptions(record.serverId);
      if (res.data.success) message.success('服务器订阅已重新加载');
      else message.error(res.data.message);
    } catch (error) { message.error(errorText(error)); }
    finally { setChangingId(null); }
  };
  const columns: ColumnsType<MqttChannel> = [
    { title: 'MQTT 服务器', key: 'server', render: (_, record) => {
      const server = servers.find(item => item.id === record.serverId);
      return server ? <Space direction="vertical" size={0}><span>{server.name}{!server.enabled && <Tag>服务器已禁用</Tag>}</span><Typography.Text type="secondary">{server.url}:{server.port}</Typography.Text></Space> : `Server #${record.serverId}`;
    } },
    { title: '频道', dataIndex: 'channelName', render: value => <Tag color="blue">{value}</Tag> },
    { title: '启用', key: 'enabled', render: (_, record) => <Switch checked={record.enabled} loading={changingId === record.id} disabled={changingId !== null} onChange={enabled => toggle(record, enabled)} /> },
    { title: '操作', key: 'actions', render: (_, record) => <Space>
      <Button type="link" icon={<ReloadOutlined />} disabled={changingId !== null} onClick={() => retry(record)}>重新加载</Button>
      <Button type="link" icon={<EditOutlined />} disabled={changingId !== null} onClick={() => open(record)}>编辑</Button>
      <Popconfirm title="删除此频道的全部订阅？" onConfirm={() => remove(record)} okText="删除" cancelText="取消">
        <Button type="link" danger icon={<DeleteOutlined />} disabled={changingId !== null}>删除</Button>
      </Popconfirm>
    </Space> },
  ];

  return <div style={{ padding: 24 }}><Card>
    <Space style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
      <Typography.Title level={3} style={{ margin: 0 }}>MQTT 频道订阅</Typography.Title>
      <Space><Button icon={<ReloadOutlined />} onClick={load} loading={loading}>刷新列表</Button><Button type="primary" icon={<PlusOutlined />} onClick={() => open()}>添加频道</Button></Space>
    </Space>
    <Typography.Paragraph type="secondary">每个频道自动订阅设备状态、命令响应、网络信息、系统信息、PPP 日志和 PPPoE 状态。保存后自动加载订阅。</Typography.Paragraph>
    <Table columns={columns} dataSource={channels} rowKey="id" loading={loading} pagination={{ pageSize: 10 }} />
  </Card>
    <Modal title={editing ? '编辑频道' : '添加频道'} open={visible} onOk={save} confirmLoading={saving} onCancel={() => { if (!saving) setVisible(false); }} destroyOnClose>
      <Form form={form} layout="vertical">
        <Form.Item name="serverId" label="MQTT 服务器" rules={[{ required: true, message: '请选择 MQTT 服务器' }]}>
          <Select placeholder="选择 MQTT 服务器" options={servers.map(server => ({ value: server.id, label: `${server.name}${server.enabled ? '' : '（已禁用）'}`, disabled: !server.enabled }))} />
        </Form.Item>
        <Form.Item name="channelName" label="频道" normalize={value => value.trim()} rules={[{ required: true, message: '请输入频道' }, { pattern: /^[a-zA-Z0-9_-]{1,50}$/, message: '使用字母、数字、下划线或连字符，最多 50 个字符' }]} extra="填写设备使用的频道，例如 client、pute、bianhecloud。">
          <Input placeholder="输入频道" maxLength={50} />
        </Form.Item>
      </Form>
    </Modal>
  </div>;
};
export default MqttSubscriptionManagement;
